import { getProjectNameByCurrentPlatform } from '../../constants/projectName';
import { container } from '../../utils/container/container';
import { GlobalAppConfig } from '../../types/windowPatching';
import { ServiceIds } from '../../utils/container/service-ids';
import * as url from 'url';

function deduplicateSlashes(urlString: string): string {
    let [protocol, restPart] = urlString.match(/(https?:\/\/)?(.*)/).slice(1, 3);
    restPart = restPart.replace(/\/+/g, '/');
    return `${protocol ?? ''}${restPart}`;
}

export class Url {
    private domain: string = 'https';
    private protocol: NetworkProtocol;
    private requestUrl: string;
    private queryParams: url.URLSearchParams;
    private hash: string;

    constructor(url?: string) {
        if (url) {
            if (!(typeof url === 'string')) {
                throw new TypeError('String expected');
            }
        }
    }

    public static getNotificationsServiceUrl(): string {
        return `${window.app.config.web_push_service.rpc}`;
    }

    public static getFullLangUrl(path: string): string {
        return this.getDeduplicatedSlashedPathByPrefix(`${window.app.config.site_url}${window.app.config.language}`, path);
    }

    public static getNoLangUrl(path: string): string {
        const siteUrl = this.getSiteUrl();
        return this.getDeduplicatedSlashedPathByPrefix(siteUrl, path);
    }

    public static getServiceWorkerUrl(path: string): string {
        const siteUrl = this.getSiteUrl();
        return deduplicateSlashes(`https:${siteUrl}/${path}`);
    }

    private static getSiteUrl(): string {
        const config = container.get<GlobalAppConfig>(ServiceIds.CONFIG);
        return config.site_url;
    }

    public static getPayserviceUrl(path:string) {
        return deduplicateSlashes(`${window.app.config.payservice_url}/${path}`);
    }

    public static getSiteLangUrl(path: string) {
        return this.getDeduplicatedSlashedPathByPrefix(`${window.app.config.game_site_url}/${window.app.config.language}`, path);
    }

    public static getSiteNoLangUrl(path: string) {
        return this.getDeduplicatedSlashedPathByPrefix(window.app.config.game_site_url, path);
    }

    public static getCurrentUrl(): string {
        return location.href;
    }

    public static getAjaxUrl(path: string): string {
        return this.getDeduplicatedSlashedPathByPrefix(`${window.app.config.site_url}${window.app.config.ajax_url}`, path);
    }

    public static getHubClientRestapiUrl():string {
        const gameName = getProjectNameByCurrentPlatform();
        return deduplicateSlashes(`https:${window.app.config.game_site_url}/restapi/${gameName}/web/`);
    }

    public static getGdprUrl():string {
        let url = Url.getNoLangUrl('delete-personal-data/auth');
        if (window.app.config.user_auth_token) {
            const params = [
                `token=${encodeURIComponent(window.app.config.user_auth_token)}`,
                `language=${encodeURIComponent(window.app.config.language)}`
            ];
            url += `?${params.join('&')}`;
        }
        return url;
    }

    public static current(): Url {
        const currentUrl = window.location.href;
        return new Url(currentUrl);
    }

    public setQueryParams(queryUrl: string = null) {
        const url = queryUrl ? queryUrl : window.location.search;
        this.queryParams = new URLSearchParams(url);
    }

    public addQueryParam(name: string, param: string) {
        if (!this.queryParams) {
            this.setQueryParams();
        }

        this.queryParams.append(name, param);
    }

    public getQueryParam(name: string): string {
        if (!this.queryParams) {
            this.setQueryParams()
        }

        return this.queryParams.get(name);
    }

    public setDomain(domain: string) {
        this.domain = domain;
    }

    public setProtocol(protocol: NetworkProtocol): void {
        this.protocol = protocol;
    }

    public setHash(hash: string) {
        this.hash = hash;
    }

    public getDomain(): string {
        return this.domain;
    }

    public getRequestUrl(): string {
        return this.requestUrl;
    }

    public getProtocol(): NetworkProtocol {
        return this.protocol;
    }

    public getHash(): string {
        return this.hash;
    }

    public toString(): string {
        return '';
    }

    public buildUrl(): string {
        const location = window.location;
        return `${location.protocol}//${location.hostname}${location.pathname}?${this.queryParams.toString()}`;
    }

    public setHref(href: string = ''): void {
        if (!href) {
            window.location.href = this.buildUrl();
            return;
        }

        window.location.href = href;
    }

    public static getDeduplicatedSlashedPathByPrefix(prefix: string, path: string): string {
        if (!path) {
            return deduplicateSlashes(`https:${prefix}/`);
        }

        const [pathWithoutFragment, fragment] = path.split('#');
        const [basePath, queryString] = pathWithoutFragment.split('?');

        let finalUrl = deduplicateSlashes(`https:${prefix}/${basePath}/`);

        if (queryString) {
            finalUrl += `?${queryString}`;
        }

        if (fragment) {
            finalUrl += `#${fragment}`;
        }

        return finalUrl;
    }
}

enum NetworkProtocol {
    HTTP = 'http',
    HTTPS = 'https'
}
